import { defineComponent as _defineComponent } from 'vue';
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, withCtx as _withCtx, createTextVNode as _createTextVNode } from "vue";
const _hoisted_1 = { class: "text-lg font-bold" };
const _hoisted_2 = { class: "flex flex-col gap-6" };
const _hoisted_3 = { class: "flex flex-col gap-4" };
const _hoisted_4 = {
    key: 0,
    class: "text-sm text-(--colors-text-body-subtle)"
};
const _hoisted_5 = {
    key: 1,
    class: "flex flex-col gap-2"
};
const _hoisted_6 = { class: "flex items-center gap-3" };
const _hoisted_7 = { class: "flex-1 min-w-0 text-sm leading-5 text-(--colors-text-body)" };
const _hoisted_8 = { class: "font-medium text-(--colors-text-body)" };
const _hoisted_9 = { class: "text-(--colors-text-body-subtle)" };
const _hoisted_10 = {
    key: 0,
    class: "text-(--colors-text-body-subtle)"
};
const _hoisted_11 = {
    key: 0,
    class: "flex items-center gap-1 shrink-0"
};
const _hoisted_12 = ["aria-label", "onClick"];
const _hoisted_13 = ["aria-label", "onClick"];
const _hoisted_14 = ["innerHTML"];
const _hoisted_15 = {
    key: 1,
    class: "flex flex-col gap-3"
};
const _hoisted_16 = { class: "flex gap-2 justify-end" };
import { Avatar, Button, ConfirmDeleteModal, Modal, RichTextEditor, WidgetShell, } from "@eudonet/opal";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import DOMPurify from "dompurify";
import { useDonationStore } from "../../../stores/useDonationStore";
import { useProfileStore } from "@/modules/settings/stores/useProfileStore";
import { useAppToast } from "@/shared/composables/useAppToast";
import { formatDateTime } from "@/shared/utils/date-formatting";
export default /*@__PURE__*/ _defineComponent({
    __name: 'DonationTabNotes',
    props: {
        donationId: {},
        comments: {}
    },
    setup(__props) {
        const { t, locale } = useI18n();
        const donationStore = useDonationStore();
        const profileStore = useProfileStore();
        const { toastSuccess, toastError } = useAppToast();
        const props = __props;
        // Translated copy for the OPAL RichTextEditor (R29 — the component ships English
        // defaults; oportal supplies the localized set via the labels prop).
        const editorLabels = computed(() => ({
            toolbar: t("finance.donations.detail.comments.editor.toolbar"),
            bold: t("finance.donations.detail.comments.editor.bold"),
            clearFormatting: t("finance.donations.detail.comments.editor.clearFormatting"),
            italic: t("finance.donations.detail.comments.editor.italic"),
            underline: t("finance.donations.detail.comments.editor.underline"),
            strikethrough: t("finance.donations.detail.comments.editor.strikethrough"),
            textColor: t("finance.donations.detail.comments.editor.textColor"),
            highlight: t("finance.donations.detail.comments.editor.highlight"),
            alignLeft: t("finance.donations.detail.comments.editor.alignLeft"),
            alignCenter: t("finance.donations.detail.comments.editor.alignCenter"),
            alignRight: t("finance.donations.detail.comments.editor.alignRight"),
            textFormat: t("finance.donations.detail.comments.editor.textFormat"),
            paragraph: t("finance.donations.detail.comments.editor.paragraph"),
            heading1: t("finance.donations.detail.comments.editor.heading1"),
            heading2: t("finance.donations.detail.comments.editor.heading2"),
            heading3: t("finance.donations.detail.comments.editor.heading3"),
            insertLink: t("finance.donations.detail.comments.editor.insertLink"),
            insertImage: t("finance.donations.detail.comments.editor.insertImage"),
            bulletList: t("finance.donations.detail.comments.editor.bulletList"),
            orderedList: t("finance.donations.detail.comments.editor.orderedList"),
            blockquote: t("finance.donations.detail.comments.editor.blockquote"),
            linkDialogTitle: t("finance.donations.detail.comments.editor.linkDialogTitle"),
            linkUrlLabel: t("finance.donations.detail.comments.editor.linkUrlLabel"),
            linkUrlPlaceholder: t("finance.donations.detail.comments.editor.linkUrlPlaceholder"),
            linkInsert: t("finance.donations.detail.comments.editor.linkInsert"),
            linkRemove: t("finance.donations.detail.comments.editor.linkRemove"),
            imageDialogTitle: t("finance.donations.detail.comments.editor.imageDialogTitle"),
            imageUrlLabel: t("finance.donations.detail.comments.editor.imageUrlLabel"),
            imageUrlPlaceholder: t("finance.donations.detail.comments.editor.imageUrlPlaceholder"),
            imageInsert: t("finance.donations.detail.comments.editor.imageInsert"),
            cancel: t("finance.donations.detail.cancel"),
            invalidUrl: t("finance.donations.detail.comments.editor.invalidUrl"),
            fontSize: t("finance.donations.detail.comments.editor.fontSize"),
            lineHeight: t("finance.donations.detail.comments.editor.lineHeight"),
            defaultStyle: t("finance.donations.detail.comments.editor.defaultStyle"),
        }));
        // Sanitize stored HTML before v-html render — defends against stored XSS when a
        // non-TipTap producer persists script/event-handler markup.
        function sanitize(html) {
            return DOMPurify.sanitize(html);
        }
        // Author label. The BFF resolves the OPUS display name at read time; when that
        // lookup fails `createdByDisplayName` is empty and `createdBy` is a raw GUID —
        // surface a localized placeholder rather than exposing the GUID to the user.
        function authorName(comment) {
            return comment.createdByDisplayName || t("finance.donations.detail.comments.unknownAuthor");
        }
        // draft / editDraft hold HTML out of RichTextEditor. The editor's empty state
        // is `<p></p>` (non-empty string but no text content), so strip tags before
        // trim-checking for "truly empty".
        function htmlIsBlank(html) {
            if (!html)
                return true;
            const text = html.replace(/<[^>]*>/g, "").trim();
            return text === "";
        }
        // ─── Composer ─────────────────────────────────────────────────────────────────
        const draft = ref("");
        const isDraftValid = computed(() => !htmlIsBlank(draft.value));
        async function handleAddComment() {
            if (!isDraftValid.value)
                return;
            try {
                await donationStore.createComment(props.donationId, draft.value);
                draft.value = "";
                toastSuccess("finance.donations.detail.comments.addSuccess");
            }
            catch {
                toastError("finance.donations.detail.comments.addError");
            }
        }
        // ─── Edit mode ────────────────────────────────────────────────────────────────
        const editingId = ref(null);
        const editDraft = ref("");
        const editOriginal = ref("");
        const editIsDirty = computed(() => editingId.value !== null && editDraft.value !== editOriginal.value);
        function beginEdit(comment) {
            editingId.value = comment.commentId;
            editDraft.value = comment.content;
            editOriginal.value = comment.content;
        }
        function startEdit(comment) {
            // Guard against silently discarding an unsaved in-flight edit on another comment.
            if (editIsDirty.value && editingId.value !== comment.commentId) {
                pendingEditComment.value = comment;
                showDiscardModal.value = true;
                return;
            }
            beginEdit(comment);
        }
        function cancelEdit() {
            editingId.value = null;
            editDraft.value = "";
            editOriginal.value = "";
        }
        async function handleSaveEdit(commentId) {
            if (htmlIsBlank(editDraft.value))
                return;
            try {
                await donationStore.updateComment(commentId, editDraft.value);
                cancelEdit();
                toastSuccess("finance.donations.detail.comments.updateSuccess");
            }
            catch {
                toastError("finance.donations.detail.comments.updateError");
            }
        }
        // ─── Discard in-flight edit confirmation ───────────────────────────────────────
        const showDiscardModal = ref(false);
        const pendingEditComment = ref(null);
        function confirmDiscard() {
            showDiscardModal.value = false;
            if (pendingEditComment.value) {
                beginEdit(pendingEditComment.value);
                pendingEditComment.value = null;
            }
        }
        function cancelDiscard() {
            showDiscardModal.value = false;
            pendingEditComment.value = null;
        }
        // ─── Delete confirmation ────────────────────────────────────────────────────────
        const showDeleteModal = ref(false);
        const pendingDeleteId = ref(null);
        function requestDelete(commentId) {
            pendingDeleteId.value = commentId;
            showDeleteModal.value = true;
        }
        async function confirmDelete() {
            if (!pendingDeleteId.value)
                return;
            const commentId = pendingDeleteId.value;
            try {
                await donationStore.deleteComment(commentId);
                if (editingId.value === commentId)
                    cancelEdit();
                toastSuccess("finance.donations.detail.comments.deleteSuccess");
            }
            catch {
                toastError("finance.donations.detail.comments.deleteError");
            }
            finally {
                showDeleteModal.value = false;
                pendingDeleteId.value = null;
            }
        }
        // ─── Date formatting ──────────────────────────────────────────────────────────
        // The BFF returns `createdAt` as an ISO-8601 UTC instant. Render it with the
        // user's preferred date/time format (R30), falling back to the locale default.
        function formatTimestamp(iso) {
            return formatDateTime(iso, locale.value, profileStore.profile?.dateFormat, profileStore.profile?.timeFormat, profileStore.profile?.timeZone);
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock(_Fragment, null, [
                _createVNode(_unref(WidgetShell), null, {
                    "before-actions": _withCtx(() => [
                        _cache[4] || (_cache[4] = _createElementVNode("span", { class: "ri-sticky-note-line text-lg text-(--colors-text-body-subtle)" }, null, -1 /* CACHED */)),
                        _createElementVNode("h2", _hoisted_1, _toDisplayString(_ctx.$t("finance.donations.detail.comments.title")), 1 /* TEXT */)
                    ]),
                    default: _withCtx(() => [
                        _createElementVNode("div", _hoisted_2, [
                            _createElementVNode("div", _hoisted_3, [
                                _createVNode(_unref(RichTextEditor), {
                                    modelValue: draft.value,
                                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((draft).value = $event)),
                                    "aria-label": _ctx.$t('finance.donations.detail.comments.composerAria'),
                                    placeholder: _ctx.$t('finance.donations.detail.comments.contentPlaceholder'),
                                    labels: editorLabels.value,
                                    "full-width": ""
                                }, null, 8 /* PROPS */, ["modelValue", "aria-label", "placeholder", "labels"]),
                                _createElementVNode("div", null, [
                                    _createVNode(_unref(Button), {
                                        variant: "primary",
                                        size: "md",
                                        label: _ctx.$t('finance.donations.detail.comments.saveBtn'),
                                        disabled: !isDraftValid.value,
                                        loading: _unref(donationStore).isSavingComment && editingId.value === null,
                                        onClick: handleAddComment
                                    }, null, 8 /* PROPS */, ["label", "disabled", "loading"])
                                ])
                            ]),
                            (__props.comments.length === 0)
                                ? (_openBlock(), _createElementBlock("p", _hoisted_4, _toDisplayString(_ctx.$t("finance.donations.detail.comments.empty")), 1 /* TEXT */))
                                : (_openBlock(), _createElementBlock("div", _hoisted_5, [
                                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.comments, (comment) => {
                                        return (_openBlock(), _createElementBlock("article", {
                                            key: comment.commentId,
                                            class: "relative bg-(--colors-background-white) border border-(--colors-border-base-soft) rounded-lg p-4 flex flex-col gap-2 shadow-[0px_1px_0.5px_0.05px_rgba(29,_41,_61,_0.02)]"
                                        }, [
                                            _createElementVNode("header", _hoisted_6, [
                                                _createVNode(_unref(Avatar), {
                                                    size: "sm",
                                                    name: authorName(comment),
                                                    presentational: ""
                                                }, null, 8 /* PROPS */, ["name"]),
                                                _createElementVNode("p", _hoisted_7, [
                                                    _createElementVNode("span", _hoisted_8, _toDisplayString(authorName(comment)), 1 /* TEXT */),
                                                    _createElementVNode("span", _hoisted_9, " · " + _toDisplayString(formatTimestamp(comment.createdAt)), 1 /* TEXT */),
                                                    (comment.updatedAt)
                                                        ? (_openBlock(), _createElementBlock("span", _hoisted_10, " · " + _toDisplayString(_ctx.$t("finance.donations.detail.comments.editedSuffix")), 1 /* TEXT */))
                                                        : _createCommentVNode("v-if", true)
                                                ]),
                                                (comment.canEdit || comment.canDelete)
                                                    ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                                                        (comment.canEdit && editingId.value !== comment.commentId)
                                                            ? (_openBlock(), _createElementBlock("button", {
                                                                key: 0,
                                                                type: "button",
                                                                class: "size-8 rounded-full flex items-center justify-center text-(--colors-text-body-subtle) hover:bg-(--colors-background-gray) focus-visible:outline-2 focus-visible:outline-(--colors-border-accent)",
                                                                "aria-label": _ctx.$t('finance.donations.detail.comments.editAria'),
                                                                onClick: ($event) => (startEdit(comment))
                                                            }, [...(_cache[5] || (_cache[5] = [
                                                                    _createElementVNode("span", { class: "ri-pencil-line text-sm" }, null, -1 /* CACHED */)
                                                                ]))], 8 /* PROPS */, _hoisted_12))
                                                            : _createCommentVNode("v-if", true),
                                                        (comment.canDelete)
                                                            ? (_openBlock(), _createElementBlock("button", {
                                                                key: 1,
                                                                type: "button",
                                                                class: "size-8 rounded-full flex items-center justify-center text-(--colors-text-body-subtle) hover:bg-(--colors-background-gray) focus-visible:outline-2 focus-visible:outline-(--colors-border-accent)",
                                                                "aria-label": _ctx.$t('finance.donations.detail.comments.deleteAria'),
                                                                onClick: ($event) => (requestDelete(comment.commentId))
                                                            }, [...(_cache[6] || (_cache[6] = [
                                                                    _createElementVNode("span", { class: "ri-delete-bin-line text-sm" }, null, -1 /* CACHED */)
                                                                ]))], 8 /* PROPS */, _hoisted_13))
                                                            : _createCommentVNode("v-if", true)
                                                    ]))
                                                    : _createCommentVNode("v-if", true)
                                            ]),
                                            (editingId.value !== comment.commentId)
                                                ? (_openBlock(), _createElementBlock("div", {
                                                    key: 0,
                                                    class: "text-sm text-(--colors-text-body) leading-5 comment-content",
                                                    innerHTML: sanitize(comment.content)
                                                }, null, 8 /* PROPS */, _hoisted_14))
                                                : (_openBlock(), _createElementBlock("div", _hoisted_15, [
                                                    _createVNode(_unref(RichTextEditor), {
                                                        modelValue: editDraft.value,
                                                        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => ((editDraft).value = $event)),
                                                        "aria-label": _ctx.$t('finance.donations.detail.comments.editContentAria'),
                                                        labels: editorLabels.value,
                                                        "full-width": ""
                                                    }, null, 8 /* PROPS */, ["modelValue", "aria-label", "labels"]),
                                                    _createElementVNode("div", _hoisted_16, [
                                                        _createVNode(_unref(Button), {
                                                            variant: "secondary",
                                                            size: "sm",
                                                            label: _ctx.$t('finance.donations.detail.cancel'),
                                                            onClick: cancelEdit
                                                        }, null, 8 /* PROPS */, ["label"]),
                                                        _createVNode(_unref(Button), {
                                                            variant: "primary",
                                                            size: "sm",
                                                            label: _ctx.$t('finance.donations.detail.comments.saveBtn'),
                                                            disabled: htmlIsBlank(editDraft.value),
                                                            loading: _unref(donationStore).isSavingComment && editingId.value === comment.commentId,
                                                            onClick: ($event) => (handleSaveEdit(comment.commentId))
                                                        }, null, 8 /* PROPS */, ["label", "disabled", "loading", "onClick"])
                                                    ])
                                                ]))
                                        ]));
                                    }), 128 /* KEYED_FRAGMENT */))
                                ]))
                        ])
                    ]),
                    _: 1 /* STABLE */
                }),
                _createVNode(_unref(Modal), {
                    modelValue: showDiscardModal.value,
                    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event) => ((showDiscardModal).value = $event)),
                    size: "sm",
                    "footer-position": "split",
                    "show-close-button": true
                }, {
                    header: _withCtx(() => [
                        _createTextVNode(_toDisplayString(_ctx.$t("finance.donations.detail.comments.discardEditTitle")), 1 /* TEXT */)
                    ]),
                    footer: _withCtx(() => [
                        _createVNode(_unref(Button), {
                            variant: "tertiary",
                            label: _ctx.$t('finance.donations.detail.comments.keepEditing'),
                            onClick: cancelDiscard
                        }, null, 8 /* PROPS */, ["label"]),
                        _createVNode(_unref(Button), {
                            variant: "primary",
                            label: _ctx.$t('finance.donations.detail.comments.discardConfirm'),
                            onClick: confirmDiscard
                        }, null, 8 /* PROPS */, ["label"])
                    ]),
                    default: _withCtx(() => [
                        _createElementVNode("p", null, _toDisplayString(_ctx.$t("finance.donations.detail.comments.discardEditMessage")), 1 /* TEXT */)
                    ]),
                    _: 1 /* STABLE */
                }, 8 /* PROPS */, ["modelValue"]),
                _createVNode(_unref(ConfirmDeleteModal), {
                    modelValue: showDeleteModal.value,
                    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event) => ((showDeleteModal).value = $event)),
                    title: _ctx.$t('finance.donations.detail.comments.deleteConfirmTitle'),
                    subtitle: _ctx.$t('finance.donations.detail.comments.deleteConfirmMessage'),
                    "confirm-label": _ctx.$t('finance.donations.detail.delete'),
                    "cancel-label": _ctx.$t('finance.donations.detail.cancel'),
                    onConfirm: confirmDelete
                }, null, 8 /* PROPS */, ["modelValue", "title", "subtitle", "confirm-label", "cancel-label"])
            ], 64 /* STABLE_FRAGMENT */));
        };
    }
});
